import { appBridge, config, isEmbedded } from '@/config';
import { IParamsApi } from '@/types/apis/params';
import { IResponseApi } from '@/types/apis/response';
import { IRule } from '@/types/newSetup.type';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { shared } from '@shoplinedev/appbridge';
const params = {
  shop: config.shop,
};

const convertParams = (input?: any) => {
  return {
    ...params,
    ...input,
  };
};

export const blackoutHolidaysApiCaller = createApi({
  reducerPath: 'blackoutHolidaysApiCaller',
  refetchOnMountOrArgChange: 30,
  baseQuery: fetchBaseQuery({
    baseUrl: 'https://www.googleapis.com/calendar/v3/calendars/',
  }),
  // get list blackout holidays
  endpoints: (builder) => ({
    getBlackoutHolidays: builder.query<IResponseApi.IGetBlackoutHolidays, IParamsApi.IBlackoutParams>({
      query: (input) => {
        return {
          url: `en.${input.countryCode}%23holiday%40group.v.calendar.google.com/events`,
          method: 'GET',
          params: {
            key: process.env.REACT_APP_GOOGLE_CALENDAR_TOKEN,
            timeMin: `${input.year}-01-01T00:00:00Z`,
            timeMax: `${input.year}-12-31T00:00:00Z`,
            singleEvents: 'TRUE',
          },
        };
      },
    }),
  }),
});

export const locationApiCaller = createApi({
  reducerPath: 'locationApiCaller',
  refetchOnMountOrArgChange: 30,
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SYNCTRACK_API_END_POINT,
    prepareHeaders: (headers) => {
      if (process.env.REACT_APP_ENV === 'production') {
        headers.set('Content-Security-Policy', `frame-ancestors https://${params.shop} https://admin.shopify.com`);
      }
      headers.set('authorization', config.urlParams);
      return headers;
    },
  }),
  // get data settings
  endpoints: (builder) => ({
    getCountries: builder.query<IResponseApi.IGetLocationCountries, IParamsApi.IGetLocationCountries>({
      query: (input) => {
        return {
          url: 'location/country/search',
          method: 'GET',
          params: input,
        };
      },
    }),
    getStates: builder.query<IResponseApi.IGetLocationStates, IParamsApi.IGetLocationStates>({
      query: (input) => {
        return {
          url: 'location/state/search',
          method: 'GET',
          params: input,
        };
      },
    }),
    checkIpLocation: builder.query<IResponseApi.ICheckIpLocation, void>({
      query: () => {
        return {
          url: 'location/ip-location',
          method: 'GET',
        };
      },
    }),
    getUnions: builder.query<IResponseApi.IUnionRes, IParamsApi.IUnionParams>({
      query: (input) => {
        return {
          url: 'location/union/search',
          method: 'GET',
          params: input,
        };
      },
    }),
  }),
});

export const cmsApiCaller = createApi({
  reducerPath: 'cmsApiCaller',
  refetchOnMountOrArgChange: 30,
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_CMS_API_END_POINT,
  }),
  endpoints: (builder) => ({
    getHolidayBanner: builder.query<IResponseApi.IGetHolidayBanner, IParamsApi.IGetHolidayBanner>({
      query: (input) => {
        return {
          url: 'banner/active',
          method: 'GET',
          params: input,
        };
      },
    }),
  }),
});

export const apiCaller = createApi({
  reducerPath: 'apiCaller',
  refetchOnMountOrArgChange: 30,
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_END_POINT,
    prepareHeaders: async (headers) => {
      if (isEmbedded) {
        const sessionToken = await shared.getSessionToken(appBridge);
        headers.set('Authorization', `Bearer ${sessionToken}`);
      }
      if (config.urlParams) {
        headers.set('Authorization-Url-Params', config.urlParams);
      }
      headers.set('ngrok-skip-browser-warning', 'on');

      return headers;
    },
  }),
  tagTypes: [
    'generalSetting',
    'shippingSetting',
    'shippingMethod',
    'shippingRule',
    'zipcodeSetting',
    'zipcodeRule',
    'zipcodeSpecificRule',
    'countryRule',
    'countryRuleSpecific',
    'vendor',
    'countRules',
    'shop-discount',
    'checkoutRule',
    'checkoutAppearance',
    'shopCheckoutShippingMethod',
    'rules',
    'ruleConditionDetail',
    'languageRules',
  ],
  // get data settings
  endpoints: (builder) => ({
    generalSettings: builder.query<IResponseApi.IGeneralSettings, void>({
      query: () => {
        return {
          url: 'shop/general-settings',
          method: 'GET',
          params: convertParams(),
        };
      },
      providesTags: ['generalSetting'],
    }),
    updateGeneralSetting: builder.mutation<IResponseApi.ICommon, IParamsApi.IUpdateGeneralSetting>({
      query: (input) => {
        return {
          url: 'shop/general-settings/update',
          method: 'PUT',
          body: convertParams(input),
        };
      },
      invalidatesTags: ['generalSetting', 'countRules'],
    }),
    getShopInfo: builder.query<IResponseApi.IShopInfo, void>({
      query: () => {
        return {
          url: 'shop/info',
          method: 'GET',
          params: convertParams(),
        };
      },
    }),
    getEmbeddedAppStatus: builder.query<IResponseApi.IEmbeddedAppStatus, void>({
      query: () => {
        return {
          url: 'shop/embedded-app-status',
          method: 'GET',
          params: convertParams(),
        };
      },
    }),
    updateOnboarding: builder.mutation<IResponseApi.ICommon, IParamsApi.IUpdateOnboarding>({
      query: (input) => {
        return {
          url: 'shop/onboarding/update',
          method: 'PUT',
          body: convertParams(input),
        };
      },
    }),

    getProducts: builder.query<IResponseApi.IGetProducts, IParamsApi.ICommon>({
      query: (input) => {
        return {
          url: 'attributes/products',
          method: 'GET',
          params: convertParams(input),
        };
      },
    }),
    getCollections: builder.query<IResponseApi.IGetCollections, IParamsApi.ICommon>({
      query: (input) => {
        return {
          url: 'attributes/collections',
          method: 'GET',
          params: convertParams(input),
        };
      },
    }),
    getVendors: builder.query<IResponseApi.IGetVendors, void>({
      query: () => {
        return {
          url: 'attributes/vendors',
          method: 'GET',
          params: convertParams(),
        };
      },
      providesTags: ['vendor'],
    }),

    getCountries: builder.query<IResponseApi.IListCountry, void>({
      query: () => {
        return {
          url: 'attributes/countries',
          method: 'GET',
          params: convertParams(),
        };
      },
    }),

    syncProductAndCollection: builder.mutation<IResponseApi.ICommon, void>({
      query: () => {
        return {
          url: 'attributes/sync',
          method: 'POST',
          body: convertParams(),
        };
      },
    }),

    getShopifyLanguage: builder.query<IResponseApi.IGetShopifyLanguages, void>({
      query: () => {
        return {
          url: 'attributes/sync-language',
          method: 'GET',
          params: convertParams(),
        };
      },
    }),

    importRules: builder.mutation<IResponseApi.ICommon, FormData>({
      query: (input) => {
        return {
          url: '/file/rule/import',
          method: 'POST',
          body: input,
        };
      },
      invalidatesTags: ['zipcodeRule', 'countRules', 'rules'],
    }),
    getListPricing: builder.query<IResponseApi.IGetAppPricing, void>({
      query: () => {
        return {
          url: '/app-pricing',
          method: 'GET',
          params: convertParams(),
        };
      },
    }),
    addDiscountCode: builder.mutation<IResponseApi.IGetAppPricing, { discountCode: string }>({
      query: (data) => {
        return {
          url: '/app-pricing/apply-code',
          method: 'PUT',
          body: convertParams(data),
        };
      },
      invalidatesTags: ['shop-discount'],
    }),
    getUrlUpdatePlan: builder.mutation<IResponseApi.IGetUrlUpdatePlan, IParamsApi.IUpdatePlan>({
      query: (input) => {
        return {
          url: '/shop/charge',
          method: 'GET',
          params: convertParams(input),
        };
      },
      invalidatesTags: [
        'generalSetting',
        'shippingMethod',
        'shippingRule',
        'zipcodeSpecificRule',
        'zipcodeRule',
        'countryRuleSpecific',
        'countryRule',
        'countRules',
        'rules',
        'checkoutRule',
      ],
    }),
    sendGA4Event: builder.mutation<IResponseApi.ICommon, IParamsApi.ISendGA4Event>({
      query: (input) => {
        return {
          url: '/analytics/analytic/ga-4/event',
          method: 'POST',
          body: convertParams(input),
        };
      },
    }),

    ruleCount: builder.query<IResponseApi.IGetCountRules, void>({
      query: () => {
        return {
          url: '/rule/count',
          method: 'GET',
          params: convertParams(),
        };
      },
      providesTags: [{ type: 'countRules' }],
    }),

    trackOnboarding: builder.mutation<void, IParamsApi.ITrackOnboarding>({
      query: (input) => {
        return {
          url: '/analytics/analytic/onboarding',
          method: 'POST',
          body: convertParams(input),
        };
      },
    }),

    trackTouchpoint: builder.mutation<IResponseApi.ICommon, IParamsApi.ITrackTouchpoint>({
      query: (data) => {
        return {
          url: '/analytics/analytic/touchpoint',
          method: 'POST',
          body: convertParams(data),
        };
      },
    }),

    getShopDiscount: builder.query<IResponseApi.IShopDiscountRes, void>({
      query: () => {
        return {
          url: '/app-pricing/shop-discount',
          method: 'GET',
          params: convertParams(),
        };
      },
      providesTags: [{ type: 'shop-discount' }],
    }),

    removeShopDiscount: builder.mutation<IResponseApi.ICommon, void>({
      query: () => {
        return {
          url: '/app-pricing/remove-discount',
          method: 'DELETE',
          params: convertParams(),
        };
      },
      invalidatesTags: ['shop-discount'],
    }),

    getCannyToken: builder.query<IParamsApi.ICannyResponse, void>({
      query: () => {
        return {
          url: '/shop/canny-token',
          method: 'GET',
          params: convertParams(),
        };
      },
    }),

    getShopShippingMethods: builder.query<IResponseApi.IShopShipping, void>({
      query: () => {
        return {
          url: '/shop/shipping-methods',
          method: 'GET',
          params: convertParams(),
        };
      },
      providesTags: [{ type: 'shopCheckoutShippingMethod' }],
    }),

    getShopShippingMethodsUsed: builder.query<IResponseApi.IShopShipping, void>({
      query: () => {
        return {
          url: '/rule/checkout/names-shipping-method',
          method: 'GET',
          params: convertParams(),
        };
      },
      providesTags: [{ type: 'shopCheckoutShippingMethod' }],
    }),

    getCheckoutRules: builder.query<IResponseApi.IGetCheckoutRules, IParamsApi.IGetCheckoutRules>({
      query: (input) => {
        return {
          url: '/rule/checkout/search',
          method: 'GET',
          params: convertParams(input),
        };
      },
      providesTags: ['checkoutRule'],
    }),

    getDetailCheckoutRule: builder.query<IResponseApi.IGetDetailCheckoutRule, number>({
      query: (id) => {
        return {
          url: '/rule/checkout/detail',
          method: 'GET',
          params: convertParams({ id }),
        };
      },
    }),

    createCheckoutRule: builder.mutation<IResponseApi.ICommon, IParamsApi.ICreateCheckoutRule>({
      query: (data) => {
        return {
          url: '/rule/checkout/create',
          method: 'POST',
          body: convertParams(data),
        };
      },
      invalidatesTags: ['checkoutRule', 'shopCheckoutShippingMethod'],
    }),

    updateCheckoutRule: builder.mutation<IResponseApi.ICommon, IParamsApi.IUpdateCheckoutRule>({
      query: (data) => {
        return {
          url: '/rule/checkout/update',
          method: 'PUT',
          body: convertParams(data),
        };
      },
      invalidatesTags: ['checkoutRule', 'shopCheckoutShippingMethod'],
    }),

    removeCheckoutRule: builder.mutation<IResponseApi.ICommon, number>({
      query: (id) => {
        return {
          url: '/rule/checkout/remove',
          method: 'DELETE',
          params: convertParams({ id }),
        };
      },
      invalidatesTags: ['checkoutRule', 'shopCheckoutShippingMethod'],
    }),

    bulkUpdateCheckoutRule: builder.mutation<IResponseApi.ICommon, IParamsApi.IBulkUpdateCheckoutRule>({
      query: (data) => {
        return {
          url: '/rule/checkout/bulk-update',
          method: 'PUT',
          body: convertParams(data),
        };
      },
      invalidatesTags: ['checkoutRule', 'shopCheckoutShippingMethod'],
    }),

    bulkRemoveCheckoutRule: builder.mutation<IResponseApi.ICommon, number[]>({
      query: (ids) => {
        return {
          url: '/rule/checkout/bulk-remove',
          method: 'DELETE',
          params: convertParams({ ids }),
        };
      },
      invalidatesTags: ['checkoutRule', 'shopCheckoutShippingMethod'],
    }),

    getCheckoutAppearance: builder.query<IResponseApi.IGetDetailCheckoutAppearance, void>({
      query: () => {
        return {
          url: '/shop/checkout-appearance-settings',
          method: 'GET',
          params: convertParams(),
        };
      },
      providesTags: [{ type: 'checkoutAppearance' }],
    }),

    updateCheckoutAppearance: builder.mutation<IResponseApi.ICommon, IParamsApi.IUpdateCheckoutAppearance>({
      query: (data) => {
        return {
          url: '/shop/checkout-appearance-settings/update',
          method: 'PUT',
          body: convertParams(data),
        };
      },
      invalidatesTags: [{ type: 'checkoutAppearance' }],
    }),

    getCheckoutProfileId: builder.query<IResponseApi.IGetShopCheckoutId, void>({
      query: () => {
        return {
          url: '/rule/checkout/profile-id',
          method: 'GET',
          params: convertParams(),
        };
      },
    }),

    createRule: builder.mutation<IResponseApi.ICommon, IRule>({
      query: (data) => {
        return {
          url: '/rule/create',
          method: 'POST',
          body: convertParams(data),
        };
      },
      invalidatesTags: ['rules', 'ruleConditionDetail', 'countRules'],
    }),
    updateRule: builder.mutation<IResponseApi.ICommon, IRule>({
      query: (data) => {
        return {
          url: '/rule/update',
          method: 'PUT',
          body: convertParams(data),
        };
      },
      invalidatesTags: ['rules', 'ruleConditionDetail'],
    }),
    bulkUpdateRule: builder.mutation<IResponseApi.ICommon, { ids: number[]; enable?: boolean; showTextInCart?: boolean }>({
      query: (data) => {
        return {
          url: '/rule/bulk-update',
          method: 'PUT',
          body: convertParams(data),
        };
      },
      invalidatesTags: [{ type: 'rules' }],
    }),
    getRules: builder.query<IResponseApi.IGetRules, IParamsApi.IGetRules>({
      query: (data) => {
        return {
          url: '/rule',
          method: 'GET',
          params: convertParams(data),
        };
      },
      providesTags: [{ type: 'rules' }],
    }),
    removeRule: builder.mutation<IResponseApi.ICommon, number>({
      query: (id) => {
        return {
          url: '/rule/remove',
          method: 'DELETE',
          params: convertParams({ id }),
        };
      },
      invalidatesTags: ['rules', 'countRules'],
    }),
    bulkRemoveRule: builder.mutation<IResponseApi.ICommon, string>({
      query: (ids) => {
        return {
          url: '/rule/remove-all',
          method: 'DELETE',
          params: convertParams({ ids }),
        };
      },
      invalidatesTags: ['rules', 'countRules'],
    }),

    getRuleConditionDetail: builder.query<IResponseApi.IGetConditionDetail, number>({
      query: (id) => {
        return {
          url: '/rule/detail',
          method: 'GET',
          params: convertParams({ id }),
        };
      },
      providesTags: [{ type: 'ruleConditionDetail' }],
    }),

    getLanguageRules: builder.query<IResponseApi.IGetLanguageRules, IParamsApi.ICommon>({
      query: (data) => {
        return {
          url: '/language',
          method: 'GET',
          params: convertParams(data),
        };
      },
      providesTags: ['languageRules'],
    }),

    createLanguageRule: builder.mutation<IResponseApi.ICommon, IParamsApi.ICreateLanguageRule>({
      query: (data) => {
        return {
          url: '/language/create',
          method: 'POST',
          body: convertParams(data),
        };
      },
      invalidatesTags: ['languageRules'],
    }),

    updateLanguageRule: builder.mutation<IResponseApi.ICommon, IParamsApi.IUpdateLanguageRule>({
      query: (data) => {
        return {
          url: '/language/update',
          method: 'PUT',
          body: convertParams(data),
        };
      },
      invalidatesTags: ['languageRules'],
    }),

    removeLanguageRule: builder.mutation<IResponseApi.ICommon, number>({
      query: (id) => {
        return {
          url: '/language/remove',
          method: 'DELETE',
          params: convertParams({ id }),
        };
      },
      invalidatesTags: ['languageRules'],
    }),

    bulkUpdateLanguageRule: builder.mutation<IResponseApi.ICommon, { ids: string; active?: boolean }>({
      query: (data) => {
        return {
          url: '/language/bulk-update',
          method: 'PUT',
          body: convertParams(data),
        };
      },
      invalidatesTags: ['languageRules'],
    }),

    bulkRemoveLanguageRule: builder.mutation<IResponseApi.ICommon, string>({
      query: (ids) => {
        return {
          url: '/language/bulk-remove',
          method: 'DELETE',
          params: convertParams({ ids }),
        };
      },
      invalidatesTags: ['languageRules'],
    }),

    sendEventCustomerIo: builder.mutation<IResponseApi.ICommon, { event: string }>({
      query: (data) => {
        return {
          url: '/customer-io/send-event',
          method: 'POST',
          body: convertParams(data),
        };
      },
    }),

    redirectUpdateShopScope: builder.mutation<IResponseApi.IRedirectUpdateShopScope, IParamsApi.IRedirectUpdateShopScope>({
      query: (input) => {
        return {
          url: '/shop/redirect-update-shop-scope',
          method: 'POST',
          params: convertParams(input),
        };
      },
    }),

    getDataAnalytic: builder.query<IResponseApi.IGetAnalytic, IParamsApi.IGetAnalytic>({
      query: (data) => {
        const params = {
          startDate: data.startDate,
          endDate: data.endDate,
        };
        return {
          url: '/analytics/analytic/rule',
          method: 'GET',
          params: convertParams(params),
        };
      },
    }),

    getDataProductAnalytic: builder.query<IResponseApi.IGetProductAnalytic, void>({
      query: () => {
        return {
          url: '/analytics/analytic/productAppliedAndActiveRule',
          method: 'GET',
          params: convertParams(),
        };
      },
    }),

    getProductTags: builder.query<IResponseApi.IGetProductTags, IParamsApi.ICommon>({
      query: (input) => {
        return {
          url: 'attributes/tags',
          method: 'GET',
          params: convertParams(input),
        };
      },
    }),

    getProductSKUs: builder.query<IResponseApi.IGetProductSKUs, IParamsApi.ICommon>({
      query: (input) => {
        return {
          url: 'attributes/skus',
          method: 'GET',
          params: convertParams(input),
        };
      },
    }),

    getInventoryLocations: builder.query<IResponseApi.IGetInventoryLocation, IParamsApi.ICommon>({
      query: (input) => {
        return {
          url: 'attributes/locations',
          method: 'GET',
          params: convertParams(input),
        };
      },
    }),

    getProductMetafield: builder.query<IResponseApi.IGetProductMetafield, IParamsApi.ICommon>({
      query: (input) => {
        return {
          url: '/attributes/metafieldsAllProducts',
          method: 'GET',
          params: convertParams(input),
        };
      },
    }),

    getVariantMetafield: builder.query<IResponseApi.IGetProductMetafield, IParamsApi.ICommon>({
      query: (input) => {
        return {
          url: '/attributes/metafieldsAllVariants',
          method: 'GET',
          params: convertParams(input),
        };
      },
    }),
  }),
});
